/**
 * ============================================================================
 * ABYSS VOID COLLAPSE (voidCollapse.ts)
 * ============================================================================
 * Triggered when 'X' is pressed.
 *
 * Sequence:
 * 1. Attempts to close the browser tab (window.close()).
 * 2. If blocked by browser sandbox policy (standard browser behavior),
 *    executes the abyss gravity collapse:
 *    - All visual components (header logo, each individual letter, each thumbtack,
 *      every single decal/RandomThing, every artwork grid thumbnail, and UI chrome)
 *      detach and plummet off the screen with randomized gravity acceleration
 *      and angular tumbling.
 *    - The plaster wall background slowly fades into pitch black.
 *    - No restore button or modal; refreshing the page is the only way back.
 * ============================================================================
 */

export function triggerVoidCollapse(): void {
  if (typeof window === 'undefined') return;
  if ((window as any).__SCHZ_COLLAPSED__) return;
  (window as any).__SCHZ_COLLAPSED__ = true;

  // 1. First attempt tab closure
  try {
    window.close();
  } catch (err) {
    console.warn('Tab closure blocked by browser policy:', err);
  }

  // 2. Lock scroll and begin background blackout on body
  document.body.classList.add('schz-void-collapse');
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';

  // 3. Dispatch global collapse event in case any component wants to react
  window.dispatchEvent(new CustomEvent('schz-void-collapse'));

  // 4. Query all distinct visual targets across the entire page
  const selectors = [
    '.header-image',
    '.paper-scrap',
    '.thumbtack',
    '.random-thing',
    '.decal-item',
    '.grid-image',
    '.image-row img',
    '.instagram-link',
    '.copyright-notice',
    '#image-frame',
    '#museum-card',
    '.alt-wrapper',
    '#return',
    '.info-modal-backdrop',
    '.info-modal-container',
    '#schz-info-dialog',
    '.schz-info-dialog',
    '.color-filter-track.canvas-board',
    '.canvas-board'
  ];

  const elements = Array.from(
    document.querySelectorAll<HTMLElement>(selectors.join(', '))
  );

  const vh = window.innerHeight;

  // 5. Apply individual tumbling gravity trajectories to every element
  elements.forEach((el) => {
    const rect = el.getBoundingClientRect();

    // Vertical fall distance: guaranteed to travel completely past the bottom of the screen
    const distanceToDrop = Math.max(vh - rect.top, 0) + vh + 600 + Math.random() * 400;

    // Lateral drift: random drift to left or right (-220px to +220px)
    const driftX = (Math.random() - 0.5) * 440;

    // Angular tumble: chaotic rotation (-240deg to +240deg)
    const rot = (Math.random() - 0.5) * 480;

    // Staggered release: cascading structural failure across the wall (0 to 450ms)
    const delay = Math.random() * 0.45;

    // Duration of fall: 1.1s to 1.75s
    const duration = 1.1 + Math.random() * 0.65;

    // Accelerating gravity curve
    const timingFn = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';

    const applyStyles = () => {
      // Set CSS variables for CSS stylesheet fallbacks
      el.style.setProperty('--fall-dist', `${distanceToDrop.toFixed(1)}px`);
      el.style.setProperty('--fall-drift-x', `${driftX.toFixed(1)}px`);
      el.style.setProperty('--fall-rot', `${rot.toFixed(1)}deg`);
      el.style.setProperty('--fall-delay', `${delay.toFixed(2)}s`);
      el.style.setProperty('--fall-duration', `${duration.toFixed(2)}s`);

      // Set direct inline styles with !important priority
      el.style.setProperty(
        'transition',
        `transform ${duration.toFixed(2)}s ${timingFn} ${delay.toFixed(2)}s, opacity ${duration.toFixed(2)}s ease-in ${(delay + 0.35).toFixed(2)}s`,
        'important'
      );
      el.style.setProperty(
        'transform',
        `translate3d(${driftX.toFixed(1)}px, ${distanceToDrop.toFixed(1)}px, 0) rotate(${rot.toFixed(1)}deg)`,
        'important'
      );
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('user-select', 'none', 'important');
      el.style.setProperty('will-change', 'transform', 'important');
    };

    applyStyles();
    // Re-assert in RAF and timeout to ensure no asynchronous React render reconciler wipes the properties
    requestAnimationFrame(applyStyles);
    setTimeout(applyStyles, 60);
  });
}
